import { useEffect, useState } from "react";
import StopIcon from "@mui/icons-material/Stop";
import Button from "../../utils/Button";
import { useApiQuery } from "../../hooks/useApiQuery";

export type DraftBatchSummary = {
  id: number;
  claimedForms: number;
};

type SupervisorOption = {
  id: number;
  fullName: string;
  email: string;
};

type SupervisorsResponse = {
  success: boolean;
  message: string;
  data: SupervisorOption[];
};

type DraftBatchSubmissionModalProps = {
  draftBatch: DraftBatchSummary;
  isSubmitting: boolean;
  title?: string;
  description?: string;
  helperText?: string;
  submitLabel?: string;
  submittingLabel?: string;
  onClose: () => void;
  onSubmit: (supervisorId: number | null) => void;
};

const DraftBatchSubmissionModal = ({
  draftBatch,
  isSubmitting,
  title = "Submit your draft batch",
  description = "Submit this paper-form draft batch to your supervisor for acknowledgement.",
  helperText = "Choose the supervisor who should receive and acknowledge this batch.",
  submitLabel = "Submit batch",
  submittingLabel = "Submitting...",
  onClose,
  onSubmit,
}: DraftBatchSubmissionModalProps) => {
  const [selectedSupervisorId, setSelectedSupervisorId] = useState<number | null>(null);

  const { data: supervisorsData, isLoading: supervisorsLoading } = useApiQuery<SupervisorsResponse>({
    queryKey: ["partner", "supervisors"],
    request: {
      method: "GET",
      url: "/partner/supervisors",
    },
    enabled: true,
  });

  const supervisors = supervisorsData?.data ?? [];

  useEffect(() => {
    if (supervisors.length === 1) {
      setSelectedSupervisorId(supervisors[0].id);
    }
  }, [supervisors]);

  const requiresSupervisorSelection = supervisors.length > 1;
  const hasNoSupervisors = !supervisorsLoading && supervisors.length === 0;

  return (
    <div className="fixed inset-0 z-[999999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={() => {
          if (!isSubmitting) {
            onClose();
          }
        }}
      />
      <div className="relative z-10 w-full max-w-xl rounded-2xl bg-white p-6 text-gray-900 shadow-xl">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl bg-amber-100 p-3 text-amber-700">
            <StopIcon />
          </div>
          <div>
            <h3 className="text-xl font-semibold">{title}</h3>
            <p className="mt-2 text-sm leading-6 text-gray-600">{description}</p>
            <p className="mt-2 text-sm leading-6 text-gray-600">{helperText}</p>
          </div>
        </div>

        <div className="mt-5 rounded-2xl border border-amber-100 bg-amber-50 p-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold">Draft batch #{draftBatch.id}</p>
              <p className="mt-1 text-xs text-gray-500">Forms currently waiting in draft status</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-semibold text-amber-700">{draftBatch.claimedForms}</p>
              <p className="text-xs uppercase tracking-[0.18em] text-gray-500">Claimed forms</p>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <label className="mb-2 block text-sm font-semibold text-gray-900">
            Select Supervisor
          </label>
          {supervisorsLoading ? (
            <div className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm">
              Loading supervisors...
            </div>
          ) : supervisors.length > 0 ? (
            <select
              value={selectedSupervisorId || ""}
              onChange={(event) => setSelectedSupervisorId(event.target.value ? Number(event.target.value) : null)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {supervisors.length > 1 && <option value="">Select supervisor</option>}
              {supervisors.map((supervisor) => (
                <option key={supervisor.id} value={supervisor.id}>
                  {supervisor.fullName} ({supervisor.email})
                </option>
              ))}
            </select>
          ) : (
            <div className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-500">
              No supervisors available
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button
            label="Back"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          />
          <Button
            label={isSubmitting ? submittingLabel : submitLabel}
            variant="primary"
            onClick={() => onSubmit(selectedSupervisorId)}
            disabled={isSubmitting || hasNoSupervisors || (requiresSupervisorSelection && !selectedSupervisorId)}
          />
        </div>
      </div>
    </div>
  );
};

export default DraftBatchSubmissionModal;
